'use client';

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Link } from '@/i18n/navigation';
import { parsePostBody } from '@/lib/parsePost';

interface Post {
  id: number;
  title: string;
  slug: string;
  date: string;
  member: string | null;
  body: string | null;
  imageUrl: string | null;
}

interface InfinitePostGridProps {
  initialPosts: Post[];
  initialPage: number;
  totalPages: number;
  memberFilter: string;
  searchQuery: string;
  platformFilter: string;
  yearFilter: string;
}

function getMemberSlug(member: string | null): { slug: string; color: string } {
  if (!member) return { slug: 'group', color: '#D4AF37' };
  const m = member.toLowerCase();
  if (m.includes('gd') || m.includes('g-dragon')) return { slug: 'GD', color: '#ED609F' };
  if (m.includes('taeyang')) return { slug: 'Taeyang', color: '#D7171E' };
  if (m.includes('top')) return { slug: 'TOP', color: '#8FC126' };
  if (m.includes('daesung')) return { slug: 'Daesung', color: '#F4E727' };
  if (m.includes('seungri')) return { slug: 'Seungri', color: '#1E92C6' };
  return { slug: 'group', color: '#D4AF37' };
}

export default function InfinitePostGrid({
  initialPosts,
  initialPage,
  totalPages,
  memberFilter,
  searchQuery,
  platformFilter,
  yearFilter,
}: InfinitePostGridProps) {
  const [posts, setPosts] = useState<Post[]>(initialPosts);
  const [page, setPage] = useState(initialPage);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(initialPage < totalPages);
  const [currentDate, setCurrentDate] = useState<{ year: string; month: string } | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const postRefs = useRef<Map<number, HTMLElement>>(new Map());

  // 月份縮寫（統一用英文）
  const monthAbbr = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // 當篩選條件變更時，重置狀態
  useEffect(() => {
    setPosts(initialPosts);
    setPage(initialPage);
    setHasMore(initialPage < totalPages);
    // 重置時也更新當前日期到第一篇貼文
    if (initialPosts.length > 0 && initialPosts[0].date) {
      const dateParts = initialPosts[0].date.split('-');
      if (dateParts.length >= 2) {
        const year = dateParts[0];
        const monthIndex = parseInt(dateParts[1], 10) - 1;
        const month = monthAbbr[monthIndex] || '';
        setCurrentDate({ year, month });
      }
    }
  }, [initialPosts, initialPage, totalPages, memberFilter, searchQuery, platformFilter, yearFilter]);

  // 追蹤滾動位置，找出目前可見的貼文日期
  useEffect(() => {
    const handleScroll = () => {
      // 只在電腦版啟用
      if (window.innerWidth < 768) {
        setCurrentDate(null);
        return;
      }

      // 目標線：視窗頂部往下 250px 的位置
      const targetLine = 250;
      let closestPostId: number | null = null;
      let closestDistance = Infinity;

      postRefs.current.forEach((element, postId) => {
        const rect = element.getBoundingClientRect();
        // 計算貼文中心點與目標線的距離
        const postCenter = rect.top + rect.height / 2;
        const distance = Math.abs(postCenter - targetLine);

        // 只考慮在視窗內或接近視窗頂部的貼文
        if (rect.bottom > 0 && rect.top < window.innerHeight && distance < closestDistance) {
          closestDistance = distance;
          closestPostId = postId;
        }
      });

      if (closestPostId !== null) {
        const closestPost = posts.find(p => p.id === closestPostId);
        if (closestPost?.date) {
          const dateParts = closestPost.date.split('-');
          if (dateParts.length >= 2) {
            const year = dateParts[0];
            const monthIndex = parseInt(dateParts[1], 10) - 1;
            const month = monthAbbr[monthIndex] || '';

            // 只在日期改變時更新，避免不必要的 re-render
            setCurrentDate(prev => {
              if (prev?.year === year && prev?.month === month) return prev;
              return { year, month };
            });
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 初始化

    return () => window.removeEventListener('scroll', handleScroll);
  }, [posts]);

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return;

    setLoading(true);
    const nextPage = page + 1;

    try {
      const params = new URLSearchParams();
      params.set('page', String(nextPage));
      if (memberFilter) params.set('member', memberFilter);
      if (searchQuery) params.set('q', searchQuery);
      if (platformFilter) params.set('platform', platformFilter);
      if (yearFilter) params.set('year', yearFilter);

      const res = await fetch(`/api/posts?${params.toString()}`);
      const data = await res.json() as { posts: Post[]; pagination: { hasMore: boolean } };

      setPosts((prev) => [...prev, ...data.posts]);
      setPage(nextPage);
      setHasMore(data.pagination.hasMore);
    } catch (error) {
      console.error('Failed to load more posts:', error);
    } finally {
      setLoading(false);
    }
  }, [page, loading, hasMore, memberFilter, searchQuery, platformFilter, yearFilter]);

  // Intersection Observer for infinite scroll
  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: '100px' }
    );

    if (loadMoreRef.current) {
      observerRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [loadMore, hasMore, loading]);

  // Distribute posts to 4 columns (Masonry effect)
  const columns: Post[][] = [[], [], [], []];
  posts.forEach((post, index) => {
    columns[index % 4].push(post);
  });

  const t = useTranslations();
  const tMembers = useTranslations('members');

  // 手機版較短，桌面版較高
  const mobileHeights = ['h-[180px]', 'h-[210px]', 'h-[190px]', 'h-[220px]'];
  const desktopHeights = ['md:h-[280px]', 'md:h-[320px]', 'md:h-[260px]', 'md:h-[300px]'];

  return (
    <div className="relative">
      {/* 浮動日期指示器 - 只在電腦版顯示 */}
      <div
        className={`hidden md:flex fixed left-6 top-1/2 -translate-y-1/2 z-50 transition-all duration-500 ease-out ${currentDate ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}
      >
        <div className="relative">
          {/* 裝飾線 */}
          <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-[2px] h-16 bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />

          <div className="pl-3">
            {/* 月份 */}
            <div
              className="text-[13px] font-medium tracking-[0.2em] text-[#D4AF37]/70 uppercase"
            >
              {currentDate?.month || ''}
            </div>
            {/* 年份 */}
            <div
              className="text-[42px] font-bold leading-none text-white/40 -mt-1"
              style={{ fontFamily: 'var(--font-klavika), sans-serif' }}
            >
              {currentDate?.year || ''}
            </div>
          </div>
        </div>
      </div>

      <div ref={gridRef} className="relative z-10 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 mb-12">
        {columns.map((column, colIndex) => (
          <div key={colIndex} className="flex flex-col gap-2 md:gap-4">
            {column.map((post, postIndex) => {
              const memberInfo = getMemberSlug(post.member);
              const memberName = memberInfo.slug === 'group' ? 'BIGBANG' : tMembers(memberInfo.slug);
              const heightIndex = (colIndex + postIndex) % 4;
              const heightClass = `${mobileHeights[heightIndex]} ${desktopHeights[heightIndex]}`;
              const parsed = parsePostBody(post.body, null);
              const displayCaption = parsed.caption || post.title.replace(/\[Social Media\]\s*/i, '').substring(0, 50);

              return (
                <div
                  key={post.id}
                  ref={(el) => {
                    if (el) postRefs.current.set(post.id, el);
                    else postRefs.current.delete(post.id);
                  }}
                >
                  <Link
                    href={`/post/${post.slug}`}
                    className="group block"
                  >
                    <div
                      className={`relative ${heightClass} bg-[#1A1A1A] rounded-lg overflow-hidden border border-white/5 hover:border-white/20 transition-colors`}
                    >
                    <img
                      src={post.imageUrl || '/bigbang-default.png'}
                      alt={post.title}
                      loading="lazy"
                      className={`w-full h-full group-hover:scale-105 transition-transform duration-500 ${
                        post.imageUrl ? 'object-cover' : 'object-contain bg-white'
                      }`}
                    />

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />

                    {/* Content */}
                    <div className="absolute bottom-0 left-0 right-0 p-2 md:p-4">
                      <p className="text-[10px] md:text-xs text-[#888888] mb-0.5 md:mb-1">{post.date}</p>
                      <p className="text-xs md:text-sm text-white font-medium line-clamp-1 md:line-clamp-2 mb-1 md:mb-2">
                        {displayCaption.length > 30 ? displayCaption.substring(0, 30) + '...' : displayCaption}
                      </p>
                      <span
                        className="inline-flex items-center gap-1 md:gap-1.5 text-[10px] md:text-xs"
                        style={{ color: memberInfo.color }}
                      >
                        <span
                          className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full"
                          style={{ backgroundColor: memberInfo.color }}
                        />
                        {memberName}
                      </span>
                      </div>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Load More Trigger */}
      <div ref={loadMoreRef} className="flex justify-center py-8">
        {loading && (
          <div className="flex items-center gap-3 text-[#888888]">
            <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
            <span className="text-sm">{t('common.loading')}</span>
          </div>
        )}
        {!hasMore && posts.length > 0 && (
          <p className="text-sm text-[#666666]">{t('archive.showingAll', { count: posts.length })}</p>
        )}
      </div>
    </div>
  );
}
